import { Chart } from "primereact/chart";
import { useEffect, useRef, useState } from "react";
import { formatNumberID } from "../../../../../utils/FormatNumber";
import { Skeleton } from "primereact/skeleton";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";

const colors = [
  "#E69F00", // orange
  "#56B4E9", // sky blue
  "#009E73", // bluish green
  "#F0E442", // yellow
  "#0072B2", // blue
  "#D55E00", // vermillion
  "#CC79A7", // reddish purple

  "#999999", // medium gray
  "#ADFF2F", // green-yellow
  "#FF1493", // deep pink
  "#00CED1", // dark turquoise
  "#FF6347", // tomato
  "#4682B4", // steel blue
  "#8A2BE2", // blue violet
  "#FF4500", // orange red
  "#A52A2A", // brown
  "#2E8B57", // sea green
  "#6A5ACD", // slate blue
  "#20B2AA", // light sea green
  "#DAA520", // goldenrod

  "#E69F00", // orange
  "#56B4E9", // sky blue
  "#009E73", // bluish green
  "#F0E442", // yellow
  "#0072B2", // blue
  "#D55E00", // vermillion
  "#CC79A7", // reddish purple

  "#999999", // medium gray
  "#ADFF2F", // green-yellow
  "#FF1493", // deep pink
  "#00CED1", // dark turquoise
  "#FF6347", // tomato
  "#4682B4", // steel blue
  "#8A2BE2", // blue violet
  "#FF4500", // orange red
  "#A52A2A", // brown
  "#2E8B57", // sea green
  "#6A5ACD", // slate blue
  "#20B2AA", // light sea green
  "#DAA520", // goldenrod

  "#E69F00", // orange
  "#56B4E9", // sky blue
  "#009E73", // bluish green
  "#F0E442", // yellow
  "#0072B2", // blue
  "#D55E00", // vermillion
  "#CC79A7", // reddish purple

  "#999999", // medium gray
  "#ADFF2F", // green-yellow
  "#FF1493", // deep pink
  "#00CED1", // dark turquoise
  "#FF6347", // tomato
  "#4682B4", // steel blue
  "#8A2BE2", // blue violet
  "#FF4500", // orange red
  "#A52A2A", // brown
  "#2E8B57", // sea green
  "#6A5ACD", // slate blue
  "#20B2AA", // light sea green
  "#DAA520", // goldenrod
];

const OwnerDashboardGeneralPieChart = (props) => {
  const { data, filterValue, loading } = props;

  const paymentMethodPieRef = useRef(null);
  const serviceVsProductPieRef = useRef(null);

  const [revenueChartByPaymentMethodData, setRevenueChartByPaymentMethodData] =
    useState({});
  const [
    revenueChartByPaymentMethodOptions,
    setRevenueChartByPaymentMethodOptions,
  ] = useState({});

  const [serviceVsProductData, setServiceVsProductData] = useState({});
  const [serviceVsProductOptions, setServiceVsProductOptions] = useState({});

  const generateDoughnutChart = () => {
    const amounts = data.revenue_by_payment_method.map(
      (item) => item.amount_collected
    );
    const labels = data.revenue_by_payment_method.map(
      (item) => item.method_name
    );
    const revenueData = {
      labels,
      datasets: [
        {
          data: amounts,
          backgroundColor: colors,
          hoverOffset: 20,
          hoverBorderColor: "#ffffff",
          hoverBorderWidth: 2,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 10, // 👈 Add padding inside the canvas so hoverOffset doesn't clip
      },
      plugins: {
        legend: {
          display: false,
        },
      },
    };

    const sortedData = data?.service_vs_product?.sort(
      (a, b) => b.revenue - a.revenue
    );

    const serviceVsProductData = {
      labels: sortedData.map((item) => item.label),
      datasets: [
        {
          data: sortedData.map((item) => item.revenue),
          backgroundColor: colors,
          hoverOffset: 20,
          hoverBorderColor: "#ffffff",
          hoverBorderWidth: 2,
        },
      ],
    };

    setServiceVsProductData(serviceVsProductData);
    setServiceVsProductOptions(options);

    setRevenueChartByPaymentMethodData(revenueData);
    setRevenueChartByPaymentMethodOptions(options);
  };

  useEffect(() => {
    if (data) generateDoughnutChart();
  }, [data]);

  const PaymentMethodsList = ({ data }) => {
    const scrollRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [atBottom, setAtBottom] = useState(false);

    useEffect(() => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;

      const checkOverflow = () => {
        const isOverflow = scrollEl.scrollHeight > scrollEl.clientHeight;
        setIsOverflowing(isOverflow);
      };

      const handleScroll = () => {
        const isAtBottom =
          scrollEl.scrollTop + scrollEl.clientHeight >=
          scrollEl.scrollHeight - 1;
        setAtBottom(isAtBottom);
      };

      checkOverflow();
      handleScroll(); // trigger on mount

      scrollEl.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", checkOverflow);

      return () => {
        scrollEl.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", checkOverflow);
      };
    }, []);

    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    return (
      <div className="flex-1 min-w-0 relative">
        <div
          ref={scrollRef}
          className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        >
          {data?.revenue_by_payment_method.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              onMouseEnter={() => {
                const chart = paymentMethodPieRef.current?.getChart?.();
                if (chart) {
                  chart.setActiveElements([{ datasetIndex: 0, index }]);
                  chart.tooltip.setActiveElements(
                    [{ datasetIndex: 0, index }],
                    { x: 0, y: 0 }
                  );
                  chart.update();
                }
              }}
              onMouseLeave={() => {
                const chart = paymentMethodPieRef.current?.getChart?.();
                if (chart) {
                  chart.setActiveElements([]);
                  chart.tooltip.setActiveElements([], { x: 0, y: 0 });
                  chart.update();
                }
              }}
            >
              {/* Left side - Method info */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div
                  className="w-7 h-3 sm:w-8 sm:h-4 rounded-sm border border-black flex-shrink-0"
                  style={{
                    backgroundColor: colors[index % colors.length],
                  }}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-black text-xs sm:text-base truncate">
                    {item.method_name}
                  </p>
                  <p className="text-black text-xs sm:text-sm">
                    {item.payments_count} transaksi
                  </p>
                </div>
              </div>

              {/* Right side - Amount */}

              <div className="text-right flex-shrink-0 ml-3">
                <p
                  className={`font-bold text-xs sm:text-base ${
                    item.method_type == 2 ? "text-red-600" : "text-black"
                  }`}
                >
                  Rp {formatNumberID(item.amount_collected)}
                </p>
              </div>
            </div>
          ))}

          {/* ↓ Scroll hint, hidden if at bottom */}
          <AnimatePresence>
            {isOverflowing && !atBottom && (
              <motion.button
                key="scroll-arrow"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.4 }}
                onClick={scrollToBottom}
                className="row-center-center absolute bottom-1 left-0 w-full"
              >
                <div className="inline-block bg-gray-300 opacity-70 text-center row-center-center px-4 py-1 rounded-full text-md gap-2">
                  <span className="text-sm text-black font-semibold">
                    Scroll to bottom
                  </span>
                  <i className="pi pi-chevron-down"></i>
                </div>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 w-full">
      {/* Pie Chart Section */}
      <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 border border-gray-300">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-1 w-full p-6 !pb-0">
          <h2 className="text-lg sm:text-xl font-semibold text-black">
            Payment Methods
          </h2>
          <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
            {format(new Date(filterValue?.dari_tanggal), "dd MMM yyyy")} -{" "}
            {format(new Date(filterValue?.sampai_tanggal), "dd MMM yyyy")}
          </span>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="space-y-6 w-full">
              {/* Chart Placeholder */}
              <div className="flex justify-center">
                <div className="w-[90%] max-w-md lg:w-80 aspect-square">
                  <Skeleton width="100%" height="100%" shape="circle" />
                </div>
              </div>

              {/* Legend Skeleton */}
              <div className="space-y-4 max-h-80 overflow-y-auto w-full">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg w-full"
                  >
                    {/* Left side */}
                    <div className="flex items-center gap-3 flex-1">
                      <Skeleton width="2rem" height="1rem" borderRadius="4px" />
                      <div className="flex flex-col gap-1 w-full">
                        <Skeleton width="50%" height="1rem" />
                        <Skeleton width="33%" height="0.75rem" />
                      </div>
                    </div>

                    {/* Right side (Amount) */}
                    <Skeleton width="5rem" height="1rem" />
                  </div>
                ))}
              </div>
            </div>
          ) : data?.revenue_by_payment_method?.length > 0 ? (
            <div className="flex flex-col gap-6">
              {/* Chart Section */}
              <div className="flex-shrink-0 flex justify-center">
                <div className="w-full max-w-md lg:w-80 row-center-center">
                  <Chart
                    ref={paymentMethodPieRef}
                    type="doughnut"
                    data={revenueChartByPaymentMethodData}
                    options={revenueChartByPaymentMethodOptions}
                    className="w-[90%] aspect-square"
                  />
                </div>
              </div>

              {/* Legend Section */}
              <PaymentMethodsList data={data}></PaymentMethodsList>
            </div>
          ) : (
            <span className="text-lg text-black">No data available...</span>
          )}
        </div>
      </div>
      <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 border border-gray-300">
        {/* Header */}

        <div className="flex flex-col items-start justify-between gap-1 w-full p-6 !pb-0">
          <h2 className="text-lg sm:text-xl font-semibold text-black">
            Sales Distribution
          </h2>
          <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
            {format(new Date(filterValue?.dari_tanggal), "dd MMM yyyy")} -{" "}
            {format(new Date(filterValue?.sampai_tanggal), "dd MMM yyyy")}
          </span>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="space-y-6 w-full">
              {/* Chart Placeholder */}
              <div className="flex justify-center">
                <div className="w-[90%] max-w-md lg:w-80 aspect-square">
                  <Skeleton width="100%" height="100%" shape="circle" />
                </div>
              </div>

              {/* Legend Skeleton */}
              <div className="space-y-4 max-h-80 overflow-y-auto w-full">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg w-full"
                  >
                    {/* Left side */}
                    <div className="flex items-center gap-3 flex-1">
                      <Skeleton width="2rem" height="1rem" borderRadius="4px" />
                      <div className="flex flex-col gap-1 w-full">
                        <Skeleton width="50%" height="1rem" />
                        <Skeleton width="33%" height="0.75rem" />
                      </div>
                    </div>

                    {/* Right side (Amount) */}
                    <Skeleton width="5rem" height="1rem" />
                  </div>
                ))}
              </div>
            </div>
          ) : data?.service_vs_product?.length > 0 ? (
            <div className="flex flex-col gap-6">
              {/* Chart Section */}
              <div className="flex-shrink-0 flex justify-center items-center">
                <div className="w-full max-w-md lg:w-80 row-center-center">
                  <Chart
                    ref={serviceVsProductPieRef}
                    type="doughnut"
                    data={serviceVsProductData}
                    options={serviceVsProductOptions}
                    className="w-[90%] aspect-square"
                  />
                </div>
              </div>

              {/* Legend Section */}
              <div className="flex-1 min-w-0">
                <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {data?.service_vs_product.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      onMouseEnter={() => {
                        const chart =
                          serviceVsProductPieRef.current?.getChart?.();
                        if (chart) {
                          chart.setActiveElements([{ datasetIndex: 0, index }]);
                          chart.tooltip.setActiveElements(
                            [{ datasetIndex: 0, index }],
                            { x: 0, y: 0 }
                          );
                          chart.update();
                        }
                      }}
                      onMouseLeave={() => {
                        const chart =
                          serviceVsProductPieRef.current?.getChart?.();
                        if (chart) {
                          chart.setActiveElements([]);
                          chart.tooltip.setActiveElements([], { x: 0, y: 0 });
                          chart.update();
                        }
                      }}
                    >
                      {/* Left side - Label */}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                          className="w-7 h-3 sm:w-8 sm:h-4 rounded-sm border border-black flex-shrink-0"
                          style={{
                            backgroundColor: colors[index % colors.length],
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-black text-xs sm:text-base truncate">
                            {item.label}
                          </p>
                        </div>
                      </div>

                      {/* Right side - Amount */}
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="font-bold text-black text-xs sm:text-base">
                          Rp {formatNumberID(item.revenue)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-lg text-black">No data available...</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboardGeneralPieChart;
